import type {InputHTMLAttributes} from 'react';

import {useState} from 'react';

import {cn} from '../../helpers/cn';

export type SliderProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'defaultValue' | 'max' | 'min' | 'onChange' | 'size' | 'step' | 'type' | 'value'
> & {
  ariaLabel?: string;
  defaultValue?: number;
  formatAriaValue?: (value: number) => string;
  max?: number;
  min?: number;
  onChange?: (value: number) => void;
  onChangeComplete?: (value: number) => void;
  step?: number;
  value?: number;
};

export function Slider({
  className,
  defaultValue,
  disabled,
  ariaLabel,
  formatAriaValue,
  max = 100,
  min = 0,
  onChange,
  onChangeComplete,
  onKeyUp,
  onPointerUp,
  step = 1,
  value,
  ...props
}: SliderProps) {
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? min);
  const currentValue = Math.min(Math.max(isControlled ? value : uncontrolledValue, min), max);
  const progress = max === min ? 0 : ((currentValue - min) / (max - min)) * 100;

  return (
    <div
      className={cn(
        'relative flex h-6 w-full items-center rounded-full',
        disabled && 'opacity-50',
        className
      )}
    >
      <div
        className="pointer-events-none absolute inset-x-0 h-1.5 overflow-hidden rounded-full bg-gray-03"
        aria-hidden
      >
        <span
          className="absolute inset-y-0 inset-s-0 rounded-full bg-orange-main"
          style={{width: `${progress}%`}}
        />
      </div>
      <input
        {...props}
        type="range"
        min={min}
        max={max}
        step={step}
        value={currentValue}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-valuenow={currentValue}
        aria-valuetext={formatAriaValue?.(currentValue)}
        className={cn(
          'absolute inset-0 h-full w-full appearance-none bg-transparent',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange',
          'disabled:cursor-not-allowed',
          '[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:bg-transparent',
          '[&::-webkit-slider-thumb]:-mt-1.25 [&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none',
          '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-orange-main',
          '[&::-webkit-slider-thumb]:bg-orange-main [&::-webkit-slider-thumb]:shadow-md',
          '[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-transparent',
          '[&::-moz-range-progress]:bg-transparent',
          '[&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2',
          '[&::-moz-range-thumb]:border-orange-main [&::-moz-range-thumb]:bg-orange-main [&::-moz-range-thumb]:shadow-md',
          disabled
            ? '[&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-disabled-fg [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-disabled-fg'
            : 'cursor-pointer [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:active:cursor-grabbing [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:active:cursor-grabbing'
        )}
        onChange={(event) => {
          const nextValue = Number(event.currentTarget.value);
          if (!isControlled) {
            setUncontrolledValue(nextValue);
          }
          onChange?.(nextValue);
        }}
        onKeyUp={(event) => {
          onKeyUp?.(event);
          onChangeComplete?.(Number(event.currentTarget.value));
        }}
        onPointerUp={(event) => {
          onPointerUp?.(event);
          onChangeComplete?.(Number(event.currentTarget.value));
        }}
      />
    </div>
  );
}
